import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { PropertyDetailSkeleton } from "@/components/properties/details/PropertyDetailSkeleton";
import { PropertyDetailHeader } from "@/components/properties/details/PropertyDetailHeader";
import { PropertyDetailContent } from "@/components/properties/details/PropertyDetailContent";
import { PropertyDetailSidebar } from "@/components/properties/details/PropertyDetailSidebar";
import { usePropertyDetail } from "@/hooks/usePropertyDetail";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { formatLocalDate } from "@/utils/dateUtils";

const PropertyDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { user, profile } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  
  const { 
    isLoading, 
    property, 
    propertyImages, 
    sellerInfo, 
    daysOnSite,
    totalImageCount,
    hasMoreImages,
    loadMoreImages,
    isLoadingMoreImages
  } = usePropertyDetail(id);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      
      try {
        const { data } = await (supabase as any)
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .maybeSingle();
        
        setIsAdmin(data !== null);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };
    
    checkAdminStatus();
  }, [user]);

  // Determine if current user can see contract expiration
  const isOwner = user?.id === property?.seller_id;
  const canSeeContractExpiration = isOwner || isAdmin;

  // Utility functions
  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return "N/A";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | null | Date): string =>
    formatLocalDate(dateString, { year: 'numeric', month: 'short', day: 'numeric' });

  // Calculate gross margin
  const calculatedGrossMargin = property?.gross_margin || 
    (property?.arv_value && property?.price ? property.arv_value - property.price : null);

  if (isLoading) {
    return <PropertyDetailSkeleton />;
  }

  if (!property) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <Link to="/seller/properties/active" className="text-godyspo-blue mr-2">
            <ChevronLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold">Property Not Found</h1>
        </div>
        <p className="text-muted-foreground">The property you're looking for doesn't exist or has been removed.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <PropertyDetailHeader 
        propertyId={property.id}
        propertyAddress={property.address}
        sellerId={property.seller_id}
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <PropertyDetailContent 
          property={property}
          propertyImages={propertyImages}
          daysOnSite={daysOnSite}
          calculatedGrossMargin={calculatedGrossMargin}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          totalImageCount={totalImageCount}
          hasMoreImages={hasMoreImages}
          onLoadMoreImages={loadMoreImages}
          isLoadingMoreImages={isLoadingMoreImages}
          showViewCount={true}
          hideContractExpiration={!canSeeContractExpiration}
          hideGrossMargin={!!(profile as any)?.hide_gross_margin}
        />
        
        <div className="lg:col-span-1">
          <PropertyDetailSidebar 
            sellerInfo={sellerInfo}
            address={property.address}
            city={property.city}
            state={property.state}
            zipCode={property.zip_code}
            zillowLink={property.zillow_link}
            propertyId={property.id}
            price={property.price}
            bedrooms={property.bedrooms}
            bathrooms={property.bathrooms}
            propertyType={property.property_type}
          />
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailPage;
